import { FC, useState } from 'react'
import {
  containsLetters,
  convertMinutes,
  generateTimeOptions,
  getisValidTime,
  getTimeDifference,
} from './utils'
import { ClockIcon } from './ClockIcon'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { FormItem } from '../Form/FormItem'
import styled from 'styled-components'
import { Select } from '@lmc/ui-kit/src/dataEntry'
import { Tooltip } from 'antd'
import { Text } from '@lmc/ui-kit/src/general'
import { theme } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'

dayjs.extend(duration)

export const DEFAULT_TIME_START = '08:00'

export const DEFAULT_TIME_END = '08:30'

export const STEP_MINUTES = 10

export const MINUTES_PER_DAY = 1440

type Props = {
  startTime?: string
  endTime?: string
  onStartTimeChanged?: (time?: string) => void
  onEndTimeChanged?: (time?: string) => void
  disableStartTime?: boolean
  disableEndTime?: boolean
}

export const TimeInput: FC<Props> = ({
  startTime,
  onStartTimeChanged,
  onEndTimeChanged,
  disableStartTime,
  disableEndTime,
}) => {
  const [opened, setOpened] = useState(false)

  const [valueSearchStart, setValueSearchStart] = useState<string>()

  const [valueSearchEnd, setValueSearchEnd] = useState<string>()

  const optionsStart = generateTimeOptions(
    valueSearchStart && valueSearchStart?.length > 4 ? 1 : STEP_MINUTES
  ).reverse()

  const optionsEnd = generateTimeOptions(
    valueSearchEnd && valueSearchEnd?.length > 4 ? 1 : STEP_MINUTES
  ).reverse()

  const { t } = useTranslation()

  const handleSearchStart = (e: string) => {
    if (containsLetters(e)) return

    if (e?.length > 2) {
      setValueSearchStart(`${e?.substring(0, 2)}:${e?.substring(3, 5)}`)

      return
    }
    if (e?.length === 5) {
      setValueSearchStart(e)
      return
    }

    setValueSearchStart(e)
  }

  const handleSearchEnd = (e: string) => {
    if (containsLetters(e)) return

    if (e?.length > 2) {
      setValueSearchEnd(`${e?.substring(0, 2)}:${e?.substring(3, 5)}`)
      return
    }
    if (getisValidTime(e)) {
      setValueSearchEnd(e)
      return
    }
    setValueSearchEnd(e)
  }

  const handleChangeStart = (newValue: unknown) => {
    if (getisValidTime(newValue as string)) {
      onStartTimeChanged?.(newValue as string)
    }
  }

  const handleChangeEnd = (newValue: unknown) => {
    if (getisValidTime(newValue as string)) {
      onEndTimeChanged?.(newValue as string)
    }
  }

  return (
    <Wrapper>
      <Tooltip
        color={theme.color.neutral.color3}
        title={
          disableStartTime ? (
            <ContentContainer>
              <Text>{t('DETAIL_CANNOT_BE_EDITED')}</Text>
            </ContentContainer>
          ) : (
            ''
          )
        }
        open={disableStartTime ? undefined : false}
        overlayInnerStyle={{ width: '450px', padding: '8px 16px' }}
      >
        <FormItem
          name="startTime"
          rules={[
            {
              required: true,
              message: `The Start time field is required`,
            },
          ]}
        >
          <Select
            options={optionsStart}
            showSearch
            allowClear
            disabled={disableStartTime}
            onSearch={handleSearchStart}
            searchValue={valueSearchStart}
            placeholder="HH:MM"
            suffixIcon={
              <IconWrapper>
                <ClockIcon />
              </IconWrapper>
            }
            onChange={handleChangeStart}
            onBlur={(e: any) => {
              const value = e?.target?.value
              if (getisValidTime(value)) {
                handleChangeStart(value)
              }
            }}
            notFoundContent="Not a valid time"
            onClear={() => onStartTimeChanged?.(undefined)}
            autoClearSearchValue={false}
          />
        </FormItem>
      </Tooltip>
      <Tooltip
        color={theme.color.neutral.color3}
        title={
          disableEndTime ? (
            <ContentContainer>
              <Text>{t('DETAIL_CANNOT_BE_EDITED')}</Text>
            </ContentContainer>
          ) : (
            ''
          )
        }
        open={disableEndTime ? undefined : false}
        overlayInnerStyle={{ width: '450px', padding: '8px 16px' }}
      >
        <FormItem
          name="endTime"
          rules={[
            {
              required: true,
              message: `The End time field is required`,
            },
          ]}
        >
          <Select
            options={optionsEnd}
            showSearch
            allowClear
            disabled={disableEndTime}
            onFocus={() => setOpened(true)}
            onDropdownVisibleChange={(e: any) => setOpened(e)}
            onSelect={(e: any) => {
              const value = e?.target?.value
              if (getisValidTime(value)) {
                setOpened(false)
                handleChangeEnd(value)
              }
            }}
            onBlur={(e: any) => {
              const value = e?.target?.value
              if (getisValidTime(value)) {
                setOpened(false)
                handleChangeEnd(e?.target?.value)
              }
            }}
            onChange={handleChangeEnd}
            optionRender={(x: any) => {
              const start = valueSearchStart || startTime

              const difference =
                start && getTimeDifference(start, x.label as string)

              const isPast = Number(difference) < 0

              const minutes = isPast
                ? MINUTES_PER_DAY - Number(difference) * -1
                : difference

              const getHelpText = (minutes?: number) => {
                const hoursAndMinutes =
                  minutes && typeof minutes === 'number'
                    ? convertMinutes(minutes)
                    : { hours: 0, minutes: 0 }

                if (
                  hoursAndMinutes.hours === 0 &&
                  hoursAndMinutes.minutes === 0
                ) {
                  return '24h'
                }

                return `(${hoursAndMinutes?.hours}h ${hoursAndMinutes?.minutes}m)`
              }

              const startEndMatch = start === x.value

              return (
                <OptionWrapper>
                  {x.label}
                  {opened && !!difference && (
                    <TimeWrapper>{getHelpText(minutes as number)}</TimeWrapper>
                  )}
                  {opened && !difference && startEndMatch && (
                    <TimeWrapper>24h</TimeWrapper>
                  )}
                </OptionWrapper>
              )
            }}
            onSearch={handleSearchEnd}
            searchValue={valueSearchEnd}
            placeholder="HH:MM"
            suffixIcon={
              <IconWrapper>
                <ClockIcon />
              </IconWrapper>
            }
            notFoundContent="Not a valid time"
            onClear={() => onEndTimeChanged?.(undefined)}
            autoClearSearchValue={false}
          />
        </FormItem>
      </Tooltip>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  gap: 10px;
`

const OptionWrapper = styled.div`
  display: flex;
`

const TimeWrapper = styled.div`
  display: flex;
  margin-left: 5px;
  color: ${(props) => props.theme.color.neutral.color5};
`

const IconWrapper = styled.div`
  margin-right: -2px;
`
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  color: ${(props) => props.theme.color.neutral.color11};
`

export default TimeInput
